import { useEffect, useState } from "react";
import { i18nCrossUtil } from "@adgm/libs";
import type { IColumnConfig, IRowData } from "@adgm/cross-models";
import { CUSTOM_RADIO_OTHER_VALUES } from "@adgm/cross-config";

/** 内容最大长度限制 */
const CONTENT_MAX_LENGTH = 500;

/**
 * 表格行编辑器逻辑Hook
 * @description 抽离公共的表单状态管理、验证逻辑和字段处理
 */
export const useRowEditor = (
  visible: boolean,
  editData: IRowData | null | undefined,
  columns: IColumnConfig[],
) => {
  const crossIntl = i18nCrossUtil.t();
  const [formData, setFormData] = useState<IRowData>({});
  // 表单验证错误状态
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (visible) {
      if (editData) {
        setFormData(editData);
      } else {
        setFormData({});
      }
      setErrors({});
    }
  }, [visible, editData]);

  /**
   * 检查值是否为空（排除 CUSTOM_RADIO_OTHER_VALUES）
   */
  const isEmptyValue = (value: any): boolean => {
    if (!value) return true;
    if (typeof value === "string" && !value.trim()) return true;
    // 如果值是 CUSTOM_RADIO_OTHER_VALUES 中的值，则认为为空
    if (CUSTOM_RADIO_OTHER_VALUES.includes(value)) return true;
    if (typeof value === "object" && value.length === 0) return true;
    return false;
  };

  const validateField = (column: IColumnConfig, value: any): string => {
    // 必填字段验证
    if (column.required && isEmptyValue(value)) {
      return crossIntl["validator.required"];
    }

    // 空值跳过后续验证
    if (isEmptyValue(value)) {
      return "";
    }

    const numValue = parseFloat(value);
    const decimalPlaces = (value.toString().split(".")[1] || "").length;

    switch (column.type) {
      case "percentage":
        // 百分比范围验证
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          return crossIntl["shareholder.validation.percentage.range"];
        }
        // 小数位数验证
        if (decimalPlaces > 2) {
          return crossIntl["shareholder.validation.percentage.decimal"];
        }
        break;
    }

    // 字符串长度验证
    if (CONTENT_MAX_LENGTH && typeof value === "string" && value.length > CONTENT_MAX_LENGTH) {
      return `Maximum ${CONTENT_MAX_LENGTH} characters allowed`;
    }

    return "";
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // 先进行单字段验证
    columns.forEach(column => {
      const error = validateField(column, formData[column.dataIndex]);
      if (error) {
        newErrors[column.dataIndex] = error;
        isValid = false;
      }
    });

    // 进行互斥组验证（至少填写一个）
    const mutuallyExclusiveGroups = new Map<string, IColumnConfig[]>();
    columns.forEach(column => {
      if (column.mutuallyExclusiveGroup) {
        if (!mutuallyExclusiveGroups.has(column.mutuallyExclusiveGroup)) {
          mutuallyExclusiveGroups.set(column.mutuallyExclusiveGroup, []);
        }
        mutuallyExclusiveGroups.get(column.mutuallyExclusiveGroup)!.push(column);
      }
    });

    // 验证每个互斥组
    mutuallyExclusiveGroups.forEach((groupColumns, groupKey) => {
      const hasValidValue = groupColumns.some(column => {
        const value = formData[column.dataIndex];
        return !isEmptyValue(value);
      });

      if (!hasValidValue) {
        // 至少填写一个字段
        groupColumns.forEach(column => {
          if (!newErrors[column.dataIndex]) {
            newErrors[column.dataIndex] = crossIntl["validator.required"];
          }
        });
        isValid = false;
      }
    });

    // 更新错误状态
    setErrors(newErrors);
    return isValid;
  };

  /**
   * 更新字段值（支持互斥逻辑）
   */
  const updateField = (dataIndex: string, value: any) => {
    // 找到对应的列配置
    const column = columns.find(col => col.dataIndex === dataIndex);

    // 更新表单数据
    setFormData(prev => {
      const newData = {
        ...prev,
        [dataIndex]: value,
      };

      // 如果字段有互斥字段，清空互斥字段的值
      if (column?.mutuallyExclusiveWith) {
        column.mutuallyExclusiveWith.forEach(exclusiveDataIndex => {
          newData[exclusiveDataIndex] = "";
        });
      }

      return newData;
    });

    // 清除该字段的错误
    if (errors[dataIndex]) {
      setErrors(prev => {
        const { [dataIndex]: _, ...rest } = prev;
        return rest;
      });
    }

    // 清除互斥字段的错误（因为已经清空了）
    if (column?.mutuallyExclusiveWith) {
      setErrors(prev => {
        const newErrors = { ...prev };
        column.mutuallyExclusiveWith!.forEach(exclusiveDataIndex => {
          delete newErrors[exclusiveDataIndex];
        });
        return newErrors;
      });
    }
  };

  /**
   * 实时验证字段
   */
  const onValidation = (column: IColumnConfig, value: any) => {
    const error = validateField(column, value);
    setErrors(prev => ({ ...prev, [column.dataIndex]: error }));
  };

  return {
    formData,
    errors,
    validateForm,
    updateField,
    onValidation,
    CONTENT_MAX_LENGTH,
  };
};
